import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { WorkorderService } from 'src/app/services/workorder.service';
import { UtilityService } from 'src/app/services/utility.service';
import Swal from 'sweetalert2';
import { UserService } from 'src/app/services/user.service';
import { NgxSpinnerService } from "ngx-spinner";
import { environment } from 'src/environments/environment';

@Component({
  selector: "app-upload-dialog",
  templateUrl: "./upload-dialog.component.html",
  styleUrls: ["./upload-dialog.component.scss"],
})
export class UploadDialogComponent implements OnInit {

  fileTypes: any[] = [];
  file: File | null = null;
  result: string = '';
  selectedFileName: string = '';
  selectedImageCode: string = '';
  description: string = '';
  isCustomUiChange: string = '';
  workorderId = this.workorderService.currentWorkorderID;
  uploading = false;
  readonly allowedExtensions = [
    ...environment.fileTypesSupported
  ];
  readonly acceptedFileTypes = this.allowedExtensions.map((ext) => `.${ext}`).join(',');
  readonly acceptedFileTypesDisplay = this.allowedExtensions.map((ext) => ext.toUpperCase()).join(', ');
  readonly maxFileSizeMb = 2;
  readonly maxFileSizeBytes = this.maxFileSizeMb * 1024 * 1024;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private workorderService: WorkorderService,
    private utilityService: UtilityService,
    private userService: UserService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.isCustomUiChange = this.userService.IsCustomUiChange;
    this.getFileTypes();
  }

  getFileTypes(): void {
    this.workorderService
      .getFileTypesForUpload(this.workorderId)
      .subscribe((response) => {
        this.fileTypes = response || [];
      });
  }

  handleFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const selectedFile = input.files[0];

    if (!this.isAllowedFileType(selectedFile.name)) {
      Swal.fire({
        text: `Invalid file type. Supported file types: ${this.acceptedFileTypesDisplay}.`,
        icon: 'error',
        confirmButtonText: 'OK'
      });
      this.resetFileSelection(input);
      return;
    }

    if (selectedFile.size > this.maxFileSizeBytes) {
      Swal.fire({
        text: `Please choose a file less than ${this.maxFileSizeMb} MB.`,
        icon: 'error',
        confirmButtonText: 'OK'
      });
      this.resetFileSelection(input);
      return;
    }

    this.file = selectedFile;
    this.selectedFileName = selectedFile.name;
    const fileReader = new FileReader();
    fileReader.onload = () => {
      this.result = fileReader.result as string;
    };
    fileReader.readAsDataURL(selectedFile);
  }

  uploadDoc(): void {
    if (!this.selectedImageCode || !this.file || !this.result || this.uploading) {
      return;
    }
    this.callUpload();
  }

  callUpload(): void {
    if (!this.file || !this.result) {
      Swal.fire({
        text: 'Please choose a valid file before uploading.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    const content = this.result.split(',')[1];
    const fileName =
      this.data.lineItemID
        ? `${this.data.lineItemID}_${this.file?.name}`
        : this.file?.name;

    const body = {
      File: fileName,
      ImageCode: this.selectedImageCode,
      User:
        this.userService.dbBBCSId
          ? this.userService.dbBBCSId
          : this.utilityService.currentUtility.BBCSUserId,
      Description: this.description,
      Content: content
    };

    this.uploading = true;
    this.spinner.show();
    this.workorderService
      .uploadFile(
        fileName,
        body,
        this.data.workOrderID
      )
      .subscribe({
        next: (res: any) => {
          this.uploading = false;
          this.spinner.hide();
          if (res.Success) {
            Swal.fire({
              text: 'File uploaded successfully!',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.dialog.closeAll();
            });
          } else {
            Swal.fire({
              text: res.ErrorMessage,
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: () => {
          this.uploading = false;
          this.spinner.hide();
          Swal.fire({
            text: 'Unable to upload file.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
  }

  private isAllowedFileType(fileName: string): boolean {
    const extension = fileName.split('.').pop();
    if (!extension) {
      return false;
    }

    return this.allowedExtensions.includes(extension.toLowerCase());
  }

  private resetFileSelection(input?: HTMLInputElement): void {
    this.file = null;
    this.result = '';
    this.selectedFileName = '';
    if (input) {
      input.value = '';
    }
  }
}